//React
import * as React from "react";
import { useEffect, useState } from "react";
//Material UI
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
//Redux
import { checkIsLoggedIn, checkIsLoginError, getToken, loginUser } from "./authSlice";
import { useSelector, useDispatch } from "react-redux";
//Other
import tjommieLogo from "../../Global/images/tjommie-logo.svg";
import { CONSOLE_GROUPS, hasAnyGroup } from "../../Global/permissions/checkPermissions";
import {
  ACCENT,
  ACCENT_GRADIENT,
  BG,
  BORDER,
  FONT_STACK,
  SURFACE,
  TEXT,
} from "../../Global/designTokens";

/**
 * Sign-in screen, built to NeuraDesigns `login/index.html` (branch S-Login).
 *
 * The fields are deliberately plain inputs with standalone uppercase labels
 * rather than MUI TextFields: the design specifies a bordered, filled, 10px
 * box with the label sitting above it, which a floating-label TextField
 * cannot express without fighting it. Values come from Global/designTokens.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fieldSx = {
  width: "100%",
  background: BG,
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "10px",
  padding: "13px 16px",
  fontSize: "0.92rem",
  fontFamily: FONT_STACK,
  color: TEXT,
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  "&::placeholder": { color: "rgba(255, 255, 255, 0.2)" },
  "&:focus": {
    borderColor: "rgba(253, 185, 64, 0.45)",
    boxShadow: "0 0 0 3px rgba(253, 185, 64, 0.07)",
  },
};

const labelSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "rgba(255, 255, 255, 0.5)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

function EyeIcon({ off }) {
  return off ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function LogIn(props) {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedin = useSelector(checkIsLoggedIn);
  const isLoginError = useSelector(checkIsLoginError);
  // The identity of the current session, not merely whether one exists — see the effect below.
  const token = useSelector(getToken);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    let email = data.get("email");
    let password = data.get("password");

    dispatch(loginUser({ email, password }));
  };
  let password_reset_url = `${process.env.REACT_APP_SECRET_API}/auth/password_reset/`;

  // Field admins hold none of the groups above, so before this they authenticated successfully
  // and then sat here with no redirect and no message — the button looked broken. Userport is
  // the estate's single front door, so route them on to their own app the way the Installer
  // frontend's login already routes by group.
  //
  // Checked only after the console list, so a user holding both a console group and
  // dsm_field_admin still lands on the console. That preserves today's behaviour for everyone
  // who already has somewhere to go, and only changes the case that currently goes nowhere.
  const field_admin_url = process.env.REACT_APP_FIELD_ADMIN_URL;

  // Keyed on the token, because isLoggedin cannot detect a login that has just happened.
  // initialState sets it to true whenever an `access` cookie is present, whoever it belongs to,
  // so arriving here with a stale session means signing in flips neither isLoggedin (true ->
  // true) nor isLoginError (false -> false). The effect never re-ran and the redirect never
  // fired, which is why the dashboard only appeared after a manual refresh — a refresh reboots
  // the store and re-reads the new cookies. The token is re-minted on every login, so it is the
  // one value here that reliably changes when a sign-in succeeds.
  useEffect(() => {
    if (!isLoggedin) return;
    if (hasAnyGroup(CONSOLE_GROUPS)) {
      navigate("/");
    } else if (hasAnyGroup(["dsm_field_admin"]) && field_admin_url) {
      // A different origin, so a full page navigation rather than the router. The session
      // travels with them: cookies are scoped to the domain and ignore the port.
      window.location.href = field_admin_url;
    }
  }, [isLoggedin, isLoginError, token]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <Box sx={{ marginBottom: "28px", animation: `${fadeUp} 0.45s ease-out both` }}>
        <Box
          component="img"
          src={tjommieLogo}
          alt="Tjommie"
          sx={{ height: "44px", display: "block", margin: "0 auto" }}
        />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          width: "100%",
          maxWidth: "400px",
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: "16px",
          padding: "32px",
          textAlign: "left",
          animation: `${fadeUp} 0.5s 0.08s ease-out both`,
        }}
      >
        <Box sx={{ marginBottom: "24px" }}>
          <Typography
            component="h1"
            sx={{ fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            Sign in to continue
          </Typography>
          <Typography
            sx={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.45)", marginTop: "4px" }}
          >
            Use your Neura staff account credentials.
          </Typography>
        </Box>

        <Collapse in={isLoginError}>
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.22)",
              borderRadius: "8px",
              padding: "10px 14px",
              fontSize: "0.82rem",
              color: "#F87171",
              marginBottom: "20px",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Invalid email or password. Please try again.
          </Box>
        </Collapse>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
          <Box component="label" htmlFor="email" sx={labelSx}>
            Email address
          </Box>
          <Box
            component="input"
            id="email"
            name="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            required
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box component="label" htmlFor="password" sx={labelSx}>
            Password
          </Box>
          <Box sx={{ position: "relative" }}>
            <Box
              component="input"
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              autoComplete="current-password"
              required
              sx={{ ...fieldSx, paddingRight: "44px" }}
            />
            <Box
              component="button"
              type="button"
              onClick={() => setShowPassword((shown) => !shown)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              sx={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(255, 255, 255, 0.35)",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                "&:hover": { color: "rgba(255, 255, 255, 0.7)" },
              }}
            >
              <EyeIcon off={showPassword} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <Box
            component="button"
            type="submit"
            sx={{
              width: "100%",
              padding: "15px",
              background: ACCENT_GRADIENT,
              color: BG,
              border: "none",
              borderRadius: "12px",
              fontSize: "0.95rem",
              fontWeight: 700,
              fontFamily: FONT_STACK,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "opacity 0.15s, transform 0.15s",
              "&:hover": { opacity: 0.92, transform: "translateY(-1px)" },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            Sign in
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Box>

          <Box
            component="a"
            href={password_reset_url}
            sx={{
              fontSize: "0.82rem",
              color: "rgba(253, 185, 64, 0.75)",
              textAlign: "center",
              textDecoration: "none",
              "&:hover": { color: ACCENT },
            }}
          >
            Forgot password?
          </Box>
        </Box>
      </Box>

      <Typography
        sx={{
          marginTop: "28px",
          fontSize: "0.75rem",
          color: "rgba(255, 255, 255, 0.2)",
          animation: `${fadeUp} 0.5s 0.24s ease-out both`,
        }}
      >
        Powered by Cauchy Consult
      </Typography>
    </Box>
  );
}
